import type { RouteLocationNormalized, Router } from 'vue-router';
import { nextTick } from 'vue';
import { START_LOCATION } from 'vue-router';
import { applyRouteDocumentTitle } from '@/js/account/app/router/document-title';
import {
  AccountRouteName,
  AuthRouteName,
} from '@/js/account/app/router/route-names';
import { useAuthStore } from '@/js/account/entities/session';
import { setAccountUnauthorizedHandler } from '@/js/account/shared/api';
import { resolveSafeRedirectPath } from '@/js/account/shared/lib';
import { trackPageView } from '@/js/lib/analytics';

function isGuestRoute(route: RouteLocationNormalized): boolean {
  return route.matched.some(record => record.meta.guest === true);
}

function loginRedirectQuery(fullPath: string): { redirect: string } | undefined {
  const safe = resolveSafeRedirectPath(fullPath, '');

  if (safe === '') {
    return undefined;
  }

  return { redirect: safe };
}

export function setupRouterGuards(router: Router): void {
  const authStore = useAuthStore();

  // Guards against a burst of parallel 401s (a profile page fires several API
  // calls at once) each starting its own redirect and racing the route
  // teardown.
  let loginRedirectInFlight = false;

  setAccountUnauthorizedHandler(() => {
    authStore.clearUser();

    if (isGuestRoute(router.currentRoute.value)) {
      return;
    }

    if (loginRedirectInFlight) {
      return;
    }

    loginRedirectInFlight = true;

    // Redirect on the next tick so `clearUser()` above finishes flushing
    // before the nested <RouterView> teardown starts. Interleaving both
    // reactive cascades in one tick crashed Vue's unmount on production
    // (update on an unmounted component) and wedged routing. `.catch()` keeps
    // an expected redirect/duplicate navigation from surfacing as an unhandled
    // rejection.
    void nextTick()
      .then(async () => {
        await router.push({
          name: AuthRouteName.Login,
          query: loginRedirectQuery(router.currentRoute.value.fullPath),
        });
      })
      .catch(() => {})
      .finally(() => {
        loginRedirectInFlight = false;
      });
  });

  router.beforeEach(async (to) => {
    await authStore.bootstrap();

    const hasUser = authStore.user !== null;
    const needsVerification = authStore.needsEmailVerification;
    const isAuthenticated = hasUser || needsVerification;

    if (to.meta.guest) {
      if (hasUser) {
        return { name: AccountRouteName.Profile, replace: true };
      }

      if (needsVerification) {
        return { name: AuthRouteName.VerifyEmail, replace: true };
      }

      return true;
    }

    if (to.meta.requiresAuth && !isAuthenticated) {
      return {
        name: AuthRouteName.Login,
        query: loginRedirectQuery(to.fullPath),
      };
    }

    if (to.meta.requiresVerified) {
      if (!isAuthenticated) {
        return {
          name: AuthRouteName.Login,
          query: loginRedirectQuery(to.fullPath),
        };
      }

      if (needsVerification) {
        return { name: AuthRouteName.VerifyEmail, replace: true };
      }
    }

    if (to.meta.requiresAdmin) {
      if (!hasUser || authStore.user?.isAdmin !== true) {
        return { name: AccountRouteName.Dashboard, replace: true };
      }
    }

    if (to.name === AuthRouteName.VerifyEmail && hasUser) {
      return { name: AccountRouteName.Profile, replace: true };
    }

    return true;
  });

  router.afterEach((to, from, failure) => {
    applyRouteDocumentTitle(to);

    // afterEach also runs for aborted and cancelled navigations — the 401 handler above pushes
    // over an in-flight one — and the user never saw those routes.
    if (failure !== undefined) {
      return;
    }

    // Guard redirects never reach afterEach, so a settled navigation is a single page view
    // carrying the final route.
    trackPageView({
      path: to.fullPath,
      referrer: from === START_LOCATION ? document.referrer : from.fullPath,
      routeName: typeof to.name === 'string' ? to.name : '',
    });
  });
}
